import React, { useEffect, useState, useCallback } from 'react';
import { Chip, Tooltip, CircularProgress, Box } from '@mui/material';
import {
  CheckCircle as OkIcon,
  Warning as WarningIcon,
  Error as OutOfControlIcon,
  Science as NoQcIcon,
} from '@mui/icons-material';
import qcService, { SampleQcStatus } from '../../services/qcService';

interface Props {
  sampleId: string;
  /** When provided, the chip will refresh whenever this value changes (e.g. analyst just submitted). */
  refreshKey?: string | number;
  /** Compact = small chip without label, used for table cells. */
  compact?: boolean;
}

const COLOR_MAP: Record<
  SampleQcStatus['status'],
  'success' | 'warning' | 'error' | 'default'
> = {
  IN_CONTROL: 'success',
  WARNING: 'warning',
  OUT_OF_CONTROL: 'error',
  NO_QC: 'default',
};

const LABEL_MAP: Record<SampleQcStatus['status'], string> = {
  IN_CONTROL: 'QC OK',
  WARNING: 'QC alerte',
  OUT_OF_CONTROL: 'QC hors contrôle',
  NO_QC: 'QC n/a',
};

const ICON_MAP: Record<SampleQcStatus['status'], React.ReactNode> = {
  IN_CONTROL: <OkIcon fontSize="small" />,
  WARNING: <WarningIcon fontSize="small" />,
  OUT_OF_CONTROL: <OutOfControlIcon fontSize="small" />,
  NO_QC: <NoQcIcon fontSize="small" />,
};

/**
 * Reusable status chip shown on every analyst / supervisor row for a
 * QC-tagged sample (BIS / BLANC / MRC). Polls /qc/samples/:id/status and
 * tells the user at a glance whether their readings landed in range.
 *
 * For regular ECHANTILLON samples the backend returns role=null, status=NO_QC
 * and this chip renders nothing so we don't pollute the UI.
 */
export const SampleQcStatusChip: React.FC<Props> = ({
  sampleId,
  refreshKey,
  compact = false,
}) => {
  const [data, setData] = useState<SampleQcStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!sampleId) return;
    try {
      setLoading(true);
      const res = await qcService.getSampleStatus(sampleId);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sampleId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <CircularProgress size={12} />
      </Box>
    );
  }
  if (!data) return null;
  // Hide chip entirely for regular (non-QC) samples to avoid noise.
  if (!data.role || data.role === 'ECHANTILLON') return null;
  if (data.status === 'NO_QC') {
    // Sample is BIS/BLANC/MRC but nothing has been computed yet — show
    // a neutral chip so the user knows the engine has not run.
    return (
      <Tooltip title="QC pas encore calculé pour cet échantillon">
        <Chip
          size="small"
          color="default"
          variant="outlined"
          icon={ICON_MAP.NO_QC as any}
          label={compact ? undefined : 'QC en attente'}
        />
      </Tooltip>
    );
  }

  const tooltipBody =
    data.reasons.length > 0
      ? data.reasons.join('\n')
      : 'Tous les points QC sont dans les limites de contrôle.';

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipBody}</span>}>
      <Chip
        size="small"
        color={COLOR_MAP[data.status]}
        variant={data.status === 'IN_CONTROL' ? 'outlined' : 'filled'}
        icon={ICON_MAP[data.status] as any}
        label={compact ? undefined : `${LABEL_MAP[data.status]} (${data.role})`}
      />
    </Tooltip>
  );
};

export default SampleQcStatusChip;
